import { useLayoutEffect, useRef, useState } from "react";
import FrameIssueOverlay from "@/components/audits/replay/FrameIssueOverlay";
import { objectContainCursorPosition, stickyCursorCenter } from "@/components/audits/replay/helpers";
import LiveEvaluationCursor from "@/components/domains/LiveEvaluationCursor";
import type { Finding, InteractionScreenshot, InteractionStep } from "@/types";

export type LandingDemoStep = Pick<
  InteractionStep,
  "id" | "sequence" | "action_type" | "title" | "bounding_box" | "screenshot_id"
>;

export type LandingDemoReplay = {
  viewport: string;
  screenshots: InteractionScreenshot[];
  findings: Finding[];
  steps: LandingDemoStep[];
};

export const LANDING_WALKTHROUGH_SEEK_EVENT = "landing-walkthrough-seek";

export function openSampleEvaluation(screenshotId: string | null) {
  document.getElementById("sample-evaluation")?.scrollIntoView({ behavior: "smooth" });
  window.dispatchEvent(new CustomEvent(LANDING_WALKTHROUGH_SEEK_EVENT, { detail: { screenshotId } }));
}

type Props = {
  replay: LandingDemoReplay;
};

const DEFAULT_IMAGE_ASPECT = 1440 / 1024;

function firstFindingFrameIndex(replay: LandingDemoReplay) {
  const screenshotIds = new Set(
    replay.findings.flatMap((finding) => (finding.screenshot_id ? [finding.screenshot_id] : [])),
  );
  const index = replay.screenshots.findIndex((screenshot) => screenshotIds.has(screenshot.id));
  return index < 0 ? 0 : index;
}

export default function HeroDemoPlayer({ replay }: Props) {
  const playerRef = useRef<HTMLDivElement | null>(null);
  const [frameSize, setFrameSize] = useState({ width: 0, height: 0 });
  const [imageAspect, setImageAspect] = useState(DEFAULT_IMAGE_ASPECT);
  const frameIndex = firstFindingFrameIndex(replay);
  const screenshot = replay.screenshots[frameIndex];
  const cursorCenter = stickyCursorCenter(replay.steps, replay.screenshots, frameIndex);
  const cursor = cursorCenter
    ? objectContainCursorPosition({
        containerWidth: frameSize.width,
        containerHeight: frameSize.height,
        imageAspect,
        x: cursorCenter.x,
        y: cursorCenter.y,
      })
    : null;
  const pageTitle = screenshot?.title || "Browsing";
  const currentFrameFindings = screenshot
    ? replay.findings.filter((finding) => finding.screenshot_id === screenshot.id)
    : [];

  useLayoutEffect(() => {
    const player = playerRef.current;
    if (!player || replay.screenshots.length === 0) {
      setFrameSize({ width: 0, height: 0 });
      return;
    }

    setImageAspect(DEFAULT_IMAGE_ASPECT);

    const syncSize = () => {
      const { width, height } = player.getBoundingClientRect();
      setFrameSize({ width, height });
    };
    syncSize();

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setFrameSize({ width, height });
    });
    observer.observe(player);
    return () => observer.disconnect();
  }, [replay.screenshots.length]);

  return (
    <div id="hero-demo" className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <p className="rounded-t-lg border-b border-gray-200 px-3 py-2.5 text-center text-sm text-gray-700">
        Example finding on a sample site
        <span aria-hidden="true"> · </span>
        <a
          href="#sample-evaluation"
          className="font-medium text-gray-900 underline decoration-gray-300 underline-offset-2 hover:text-gray-950 hover:decoration-gray-500"
        >
          See how we found this
        </a>
      </p>
      <div ref={playerRef} className="relative aspect-[1440/1024] w-full overflow-visible bg-slate-50">
        {screenshot?.image_url ? (
          <div className="absolute inset-0 overflow-hidden rounded-b-lg">
            <img
              src={screenshot.image_url}
              alt={pageTitle}
              className="absolute inset-0 block h-full w-full object-contain"
              onLoad={(event) => {
                const { naturalWidth, naturalHeight } = event.currentTarget;
                if (naturalWidth > 0 && naturalHeight > 0) {
                  setImageAspect(naturalWidth / naturalHeight);
                }
              }}
            />
          </div>
        ) : null}
        {cursor ? <LiveEvaluationCursor x={cursor.x} y={cursor.y} viewport={replay.viewport} ping={false} /> : null}
        {screenshot ? (
          <FrameIssueOverlay
            findings={currentFrameFindings}
            frameId={screenshot.id}
            frameSize={frameSize}
            imageAspect={imageAspect}
            compactDetail
            staticCallout
            dock="below"
            onSelectFinding={(findingId) => {
              const finding = currentFrameFindings.find((item) => item.id === findingId);
              openSampleEvaluation(finding?.screenshot_id || screenshot.id);
            }}
          />
        ) : null}
      </div>
    </div>
  );
}
